import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { AdminResetResult, ConnectionTestResult, SettingsResponse, StaffMember } from '../api/types';
import { Alert, Spinner } from '../components/common';

/** Fields grouped as an administrator thinks about them, not as they are stored. */
// Only the secret key is something a person can go and fetch. The webhook
// secret is deliberately absent: Yoco returns it once, when a webhook is
// created through their API, and shows it nowhere in the dashboard — so asking
// anyone to paste one is asking for something they cannot get. The Register
// button below obtains and stores it instead.
const PAYMENT_FIELDS: { key: string; label: string; hint?: string; placeholder?: string }[] = [
  { key: 'Yoco:SecretKey', label: 'Yoco secret key', hint: 'From app.yoco.com → Yoco Connect → Checkout API. Starts with sk_. Not the pk_ key.', placeholder: 'sk_test_…' },
  { key: 'Yoco:PublicKey', label: 'Yoco public key', hint: 'Optional — the Checkout API does not use it.', placeholder: 'pk_test_…' },
];

/**
 * Free tiers, with their SMTP details filled in. Every one of these was a
 * support question waiting to happen: "what is the host", "which port", "does
 * it need TLS". Picking a provider fills them all.
 */
const EMAIL_PRESETS: Record<string, { label: string; note: string; values: Record<string, string> }> = {
  brevo: {
    label: 'Brevo — 300 emails/day free',
    note: 'Sign up at brevo.com, then SMTP & API → SMTP. Username is your login email, password is the SMTP key (not your account password).',
    values: { 'Email:SmtpHost': 'smtp-relay.brevo.com', 'Email:SmtpPort': '587' },
  },
  gmail: {
    label: 'Gmail — 500 emails/day free',
    note: 'Needs 2-step verification on, then an App Password from myaccount.google.com/apppasswords. Your normal password will not work.',
    values: { 'Email:SmtpHost': 'smtp.gmail.com', 'Email:SmtpPort': '587' },
  },
  resend: {
    label: 'Resend — 3,000 emails/month free',
    note: 'Sign up at resend.com and create an API key. Username is literally "resend"; password is the API key.',
    values: { 'Email:SmtpHost': 'smtp.resend.com', 'Email:SmtpPort': '587', 'Email:Username': 'resend' },
  },
  mailjet: {
    label: 'Mailjet — 200 emails/day free',
    note: 'Account Settings → API Key Management. Username is the API key, password is the secret key.',
    values: { 'Email:SmtpHost': 'in-v3.mailjet.com', 'Email:SmtpPort': '587' },
  },
};

const SMS_FIELDS: { key: string; label: string; hint?: string; placeholder?: string }[] = [
  { key: 'Sms:AccountSid', label: 'Twilio Account SID', hint: 'From the Twilio console dashboard.', placeholder: 'AC…' },
  { key: 'Sms:AuthToken', label: 'Twilio Auth Token', hint: 'Same page, hidden behind Show.' },
  { key: 'Sms:FromNumber', label: 'Sending number', hint: 'International form. Must be a number you own in Twilio.', placeholder: '+27…' },
];

const EMAIL_FIELDS: { key: string; label: string; hint?: string; placeholder?: string }[] = [
  { key: 'Email:SmtpHost', label: 'SMTP host', hint: 'Leave blank and order emails are written to the server log instead.', placeholder: 'smtp.yourprovider.co.za' },
  { key: 'Email:SmtpPort', label: 'SMTP port', placeholder: '587' },
  { key: 'Email:Username', label: 'SMTP username' },
  { key: 'Email:Password', label: 'SMTP password' },
  // The single most common first-send failure: the provider rejects any From
  // address it has not verified, and the error it returns says "sender not
  // allowed" rather than "go and verify this address".
  {
    key: 'Email:FromAddress',
    label: 'Send from',
    hint: 'Must be an address you verified with the provider, or the send is rejected. A Gmail address you own is fine to start; orders@e-smoothies.co.za works once the domain is verified.',
    placeholder: 'orders@e-smoothies.co.za',
  },
  {
    key: 'Email:StoreAddress',
    label: 'Kitchen inbox',
    hint: 'Where the new-order alert goes. Separate several with commas and everyone gets a copy.',
    placeholder: 'info@e-smoothies.co.za, owner@gmail.com',
  },
];

/**
 * Admin-only. Lets the payment and email credentials be set without editing a
 * file on the server, and proves they work before a customer discovers they
 * don't. Secrets are never sent back to the browser — only a mask.
 */
export function StaffSettingsPage() {
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState<ConnectionTestResult | null>(null);

  // The action handlers are declared above where `dirty` is computed, so the
  // current value is read through a ref rather than captured stale.
  const dirtyRef = useRef(false);

  const [team, setTeam] = useState<StaffMember[] | null>(null);
  const [reset, setReset] = useState<AdminResetResult | null>(null);

  useEffect(() => {
    api
      .staffSettings()
      .then(setSettings)
      .catch((e: Error) => setError(e.message));

    api
      .staffTeam()
      .then(setTeam)
      .catch(() => setTeam([]));
  }, []);

  async function resetPasswordFor(member: StaffMember) {
    if (!window.confirm(`Send ${member.fullName} a password reset link?`)) {
      return;
    }

    setBusy(true);
    setReset(null);
    setError(null);

    try {
      setReset(await api.staffResetPassword(member.id));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(member: StaffMember, role: string) {
    setBusy(true);
    setError(null);

    try {
      await api.staffSetRole(member.id, role);
      setTeam(await api.staffTeam());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function view(key: string) {
    return settings?.settings.find((s) => s.key === key);
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(null);
    setTest(null);

    try {
      // Only what was actually typed is sent, so untouched secrets are left alone.
      const updated = await api.staffUpdateSettings(edits);
      setSettings(updated);
      setEdits({});
      setSaved('Saved. Changes take effect on the next order — no restart needed.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function runPaymentTest() {
    if (dirtyRef.current) {
      setError('Save your changes first — the test uses the saved key, not what is typed above.');
      return;
    }

    setBusy(true);
    setTest(null);
    setError(null);

    try {
      setTest(await api.staffTestPayments());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function registerWebhook() {
    if (!settings?.webhookUrl) return;

    if (dirtyRef.current) {
      setError('Save your changes first — registering uses the saved secret key.');
      return;
    }

    setBusy(true);
    setTest(null);
    setError(null);

    try {
      const result = await api.staffRegisterWebhook(settings.webhookUrl);
      setTest({ success: result.success, message: result.message, detail: result.detail });

      // The secret is saved server side, so reload to show it as set.
      if (result.secretStored) {
        setSettings(await api.staffSettings());
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function runSmsTest() {
    const to = window.prompt('Send a test SMS to which number?', '0729820176');

    if (!to) return;

    setBusy(true);
    setTest(null);
    setError(null);

    try {
      setTest(await api.staffTestSms(to));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function runEmailTest() {
    const to = window.prompt('Send a test email to which address?', view('Email:StoreAddress')?.masked ?? '');

    if (!to) return;

    setBusy(true);
    setTest(null);
    setError(null);

    try {
      setTest(await api.staffTestEmail(to));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !settings) return <Alert>{error}</Alert>;
  if (!settings) return <Spinner label="Loading settings…" />;

  const dirty = Object.keys(edits).some((k) => edits[k].trim().length > 0);
  dirtyRef.current = dirty;

  const webhookDone = settings.checklist.some((c) => c.label.includes('webhook') && c.done);

  function field({ key, label, hint, placeholder }: { key: string; label: string; hint?: string; placeholder?: string }) {
    const v = view(key);

    return (
      <label className="field" key={key}>
        <span>{label}</span>
        <input
          // Deliberately NOT type="password". These are API keys pasted in, not
          // a password typed in, and Chrome cheerfully autofilled the admin's
          // own login password into a password field here — which looked filled
          // but never reached React, so Save silently did nothing. The field is
          // empty on load and cleared after saving, so nothing sensitive lingers.
          type="text"
          value={edits[key] ?? ''}
          placeholder={v?.isSet ? (v.masked ?? 'set') : (placeholder ?? 'not set')}
          name={`setting-${key.replace(/[:.]/g, '-')}`}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-lpignore="true"
          data-1p-ignore=""
          data-form-type="other"
          onChange={(e) => setEdits({ ...edits, [key]: e.target.value })}
        />
        <span className="muted small">
          {v?.isSet ? `Currently: ${v.masked} · from ${v.source}` : 'Not set'}
          {hint ? ` · ${hint}` : ''}
        </span>
      </label>
    );
  }

  return (
    <section className="page page--narrow">
      <h1>Settings</h1>
      <p className="muted">
        Payment and email credentials. Saved values are encrypted and never shown again in full.
      </p>

      {error && <Alert>{error}</Alert>}
      {saved && <Alert tone="success">{saved}</Alert>}

      {test && (
        <Alert tone={test.success ? 'success' : 'error'}>
          <strong>{test.message}</strong>
          {test.detail && <div className="muted small">{test.detail}</div>}
        </Alert>
      )}

      <div className="panel">
        <h2 className="panel__title">Payments</h2>

        {/* One line per requirement. A blanket "cannot pay" made a saved key
            look like it had not saved at all. */}
        <ul className="checklist">
          {settings.checklist.map((item) => (
            <li key={item.label} className={item.done ? 'is-done' : 'is-todo'}>
              <span className="checklist__mark">{item.done ? '✓' : '!'}</span>
              <span>
                <strong>{item.label}</strong>
                <span className="muted small"> — {item.detail}</span>
              </span>
            </li>
          ))}
        </ul>

        <label className="field">
          <span>Provider</span>
          <select
            value={edits['Payments:Provider'] ?? settings.activeProvider}
            onChange={(e) => setEdits({ ...edits, 'Payments:Provider': e.target.value })}
          >
            <option value="Yoco">Yoco</option>
            <option value="Paystack">Paystack</option>
          </select>
        </label>

        {PAYMENT_FIELDS.map(field)}

        <button type="button" className="btn btn--ghost btn--block" disabled={busy} onClick={runPaymentTest}>
          Test the payment key
        </button>
        <p className="muted small center">
          Opens a R1 checkout and abandons it. Nothing is charged.
        </p>

        <hr className="rule" />

        <h3 className="panel__title">Webhook</h3>
        <p className="muted small">
          Yoco confirms payments by webhook. Without one, customers can pay but no order is ever
          marked paid.
        </p>
        <p className="muted small">
          <strong>There is nothing to look up.</strong> Yoco does not show a webhook secret anywhere
          — it only hands one out at the moment a webhook is created, and only once. This button
          creates it and keeps the secret for you.
        </p>
        {settings.webhookUrl && <p className="mono small">{settings.webhookUrl}</p>}

        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={busy || !settings.webhookUrl}
          onClick={registerWebhook}
        >
          {webhookDone ? 'Re-register webhook with Yoco' : 'Register webhook with Yoco'}
        </button>
        <p className="muted small center">
          Safe to press again — it replaces the existing registration rather than adding another.
        </p>

        {/* Kept only for someone migrating a secret from elsewhere. Hidden by
            default so it cannot be mistaken for a required step. */}
        <details className="advanced">
          <summary>I already have a whsec_ value</summary>
          {field({
            key: 'Yoco:WebhookSecret',
            label: 'Webhook secret',
            hint: 'Only if you are moving one across from an existing setup.',
            placeholder: 'whsec_…',
          })}
        </details>
      </div>

      <div className="panel">
        <h2 className="panel__title">Order email</h2>

        <label className="field">
          <span>Use a free provider</span>
          <select
            value=""
            onChange={(e) => {
              const preset = EMAIL_PRESETS[e.target.value];
              if (preset) setEdits({ ...edits, ...preset.values });
            }}
          >
            <option value="">Choose one to fill in the details…</option>
            {Object.entries(EMAIL_PRESETS).map(([id, p]) => (
              <option key={id} value={id}>
                {p.label}
              </option>
            ))}
          </select>
          <span className="muted small">
            {Object.values(EMAIL_PRESETS).find((p) =>
              Object.entries(p.values).every(([k, v]) => edits[k] === v),
            )?.note ?? 'Fills in the host and port. You still add the username and password.'}
          </span>
        </label>


        {!settings.emailReady && (
          <Alert tone="info">
            <strong>No SMTP host set, so no email is leaving the server.</strong> Order
            confirmations, welcome emails and password-reset links are all being written to the
            server log instead of sent — which means a customer who forgets their password
            currently cannot get back in. Pick a provider above to fix it.
          </Alert>
        )}

        {EMAIL_FIELDS.map(field)}

        <button type="button" className="btn btn--ghost btn--block" disabled={busy} onClick={runEmailTest}>
          Send a test email
        </button>
      </div>

      <div className="panel">
        <h2 className="panel__title">Order SMS</h2>

        {settings.smsReady ? (
          <Alert tone="success">SMS is on. Customers who opt in get a text at each step.</Alert>
        ) : (
          <Alert tone="info">
            SMS is off, so order updates go by email only — which is the deliberate choice here,
            not an oversight. No phone channel is free in South Africa: Twilio costs roughly{' '}
            <strong>R0.55–0.90</strong> a message, local providers about <strong>R0.12</strong>,
            and WhatsApp about <strong>R0.25</strong> since Meta ended its free tier in July 2025.
            Email covers the same order updates at no cost. Twilio gives trial credit if you want
            to try it before spending anything.
          </Alert>
        )}

        {SMS_FIELDS.map(field)}

        <button type="button" className="btn btn--ghost btn--block" disabled={busy} onClick={runSmsTest}>
          Send a test SMS
        </button>
        <p className="muted small center">
          Sends one real message, and costs what one costs.
        </p>
      </div>


      <div className="panel">
        <h2 className="panel__title">Team</h2>
        <p className="muted small">
          Anyone can register at the ordering site; promote them here to give them the board and the
          counter. The last admin cannot be demoted, so you cannot lock yourself out.
        </p>

        {reset && (
          <Alert tone={reset.emailSent ? 'success' : 'info'}>
            <strong>{reset.message}</strong>
            {/* Shown so it can be read out when someone cannot reach their
                email. It is single use and expires within the hour. */}
            <div className="mono small" style={{ marginTop: '0.5rem' }}>{reset.resetLink}</div>
          </Alert>
        )}

        {team === null ? (
          <p className="muted small">Loading…</p>
        ) : team.length === 0 ? (
          <p className="muted small">Nobody has staff access yet.</p>
        ) : (
          <ul className="results">
            {team.map((member) => (
              <li key={member.id}>
                <div className="teamrow">
                  <span>
                    <strong>{member.fullName}</strong>
                    <span className="muted small"> · {member.email}</span>
                  </span>
                  <div className="teamrow__actions">
                    <select
                      value={member.role}
                      disabled={busy}
                      onChange={(e) => changeRole(member, e.target.value)}
                    >
                      <option value="Customer">Customer</option>
                      <option value="Staff">Staff</option>
                      <option value="Admin">Admin</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={busy}
                      onClick={() => resetPasswordFor(member)}
                    >
                      Reset password
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {dirty && (
        <Alert tone="info">
          You have unsaved changes. Press <strong>Save changes</strong> before testing or
          registering the webhook — those use the saved value.
        </Alert>
      )}

      <button type="button" className="btn btn--primary btn--block" disabled={busy || !dirty} onClick={save}>
        {busy ? 'Saving…' : dirty ? 'Save changes' : 'No changes'}
      </button>

      <p className="muted small center">
        Leave a field blank to keep what is already saved. Clearing a saved value falls back to the
        server configuration.
      </p>
    </section>
  );
}
